import { memo, type ReactNode } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { CornerUpLeft, Link2 } from "lucide-react";
import { parseNoteReplyContent } from "./noteReplyToken";
import { RichContentRenderer } from "@/components/charts";
import { MarkdownTable } from "./MarkdownTable";
import { ProposalCard } from "./ProposalCard";
import { UserInputCard } from "./UserInputCard";
import { MessageToolsFooter } from "./MessageToolsFooter";
import { ChatMediaAttachment } from "./media";
import { resolveAiText } from "@/lib/ai-text";
import type { ChatMessage, RichContent } from "@/app/api/types/chat-sessions";
import { useChatMediaByMessage } from "@stockline/web";
import { useTranslation } from "react-i18next";
import { useDashboards } from "@/query/hooks/useBi";

const VIOLET_PILL_CLASS =
  "inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 align-baseline text-[0.8125em] font-medium leading-tight text-violet-700 no-underline transition-colors hover:bg-violet-100 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-900/50";

// Pull the dashboard id out of an internal `/steering/<id>` href (tolerating a
// trailing slash, query string, or hash). Returns null for any other internal
// path so those keep the raw markdown label.
function steeringDashboardId(href: string): string | null {
  const match = /^\/steering\/([^/?#]+)/.exec(href);
  return match ? match[1] : null;
}

// A `/steering/<id>` pill. Split into its own component so the BI data layer
// (`useDashboards`, with its polling subscription) is only touched when the link
// actually targets a dashboard — every other link, internal or external, stays a
// lightweight node with no BI dependency. Resolves which TAB the dashboard is
// (its 1-indexed position in the same position-sorted list the tab strip
// renders) and labels the pill "détail dans l'onglet #N", with the dashboard's
// real title as a tooltip. If the id isn't in the list yet (edge case: freshly
// created, not polled in), falls back to the raw label.
function SteeringLink({
  href,
  dashboardId,
  children,
}: Readonly<{ href: string; dashboardId: string; children?: ReactNode }>) {
  const { t } = useTranslation();
  const { data: dashboards } = useDashboards();
  const tabIndex = dashboards?.findIndex((d) => d.id === dashboardId) ?? -1;
  const dashboard = tabIndex >= 0 ? dashboards![tabIndex] : undefined;

  return (
    <Link to={href} title={dashboard?.title} className={VIOLET_PILL_CLASS}>
      <Link2 className="h-3 w-3 shrink-0" />
      {dashboard
        ? t("businessIntelligence.chat.tabLink", { n: tabIndex + 1 })
        : children}
    </Link>
  );
}

// Renders markdown links. Internal links (href starting with "/", e.g. a BI
// brief's `[voir le détail](/steering/<id>)`) go through React Router's Link
// for client-side navigation — no full page reload — and render as a distinct
// violet pill with a link icon so they stand out as an actionable in-app
// destination inside the surrounding prose. A `/steering/<id>` target renders
// the `SteeringLink` above (the only branch that reads dashboard data).
// Everything else (http(s), mailto, anchors) stays a plain anchor opening in a
// new tab, unstyled.
export function MarkdownLink({
  href,
  children,
}: Readonly<{ href?: string; children?: ReactNode }>) {
  // A single leading slash is an in-app route; a protocol-relative "//host"
  // URL is external and must NOT go through the router.
  if (href?.startsWith("/") && !href.startsWith("//")) {
    const dashboardId = steeringDashboardId(href);
    if (dashboardId) {
      return (
        <SteeringLink href={href} dashboardId={dashboardId}>
          {children}
        </SteeringLink>
      );
    }
    return (
      <Link to={href} className={VIOLET_PILL_CLASS}>
        <Link2 className="h-3 w-3 shrink-0" />
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

// Hoisted so react-markdown sees stable references — inline literals would be
// new objects every render, forcing a full remark/GFM re-parse of each bubble.
const REMARK_PLUGINS = [remarkGfm];
const MD_COMPONENTS: Components = { table: MarkdownTable, a: MarkdownLink };

interface MessageBubbleProps {
  readonly message: ChatMessage;
  readonly isStatsMode: boolean;
  readonly isWaitingForInput?: boolean;
  readonly onSendMessage?: (message: string) => void;
}

function translateProposalMessage(
  content: string,
  t: (key: string, opts?: Record<string, string>) => string,
): string {
  if (!content.startsWith("proposal.message.")) return content;
  // Handle "proposal.message.failed:error details" format
  const [key, ...rest] = content.split(":");
  const errorDetail = rest.join(":");
  const tKey = `aiProposals.message.${key.replace("proposal.message.", "")}`;
  const translated = t(tKey, errorDetail ? { error: errorDetail } : undefined);
  return translated === tKey ? content : translated;
}

function extractRichContents(message: ChatMessage): RichContent[] {
  if (!message.tool_results) return [];
  const contents: RichContent[] = [];
  for (const result of message.tool_results) {
    // Skip rich_content from request_user_input — rendered by UserInputCard instead
    if (result.tool_use_id === "request_user_input") continue;
    if (result.rich_content) {
      contents.push(result.rich_content);
    } else if (result.content) {
      try {
        const parsed = JSON.parse(result.content);
        if (parsed.rich_content) {
          contents.push(parsed.rich_content);
        }
      } catch {
        // Not JSON, ignore
      }
    }
  }
  return contents;
}

function MessageBubbleComponent({
  message,
  isStatsMode,
  isWaitingForInput,
  onSendMessage,
}: MessageBubbleProps) {
  const { t } = useTranslation();
  const isUser = message.role === "user";
  const media = useChatMediaByMessage(message.id ?? null);
  const richContents = extractRichContents(message);

  if (isUser) {
    // A "[Note reply <id>]" prefix means the user replied to an AI note —
    // render it as a pill and show only the real text in the bubble.
    const { noteId, text } = parseNoteReplyContent(message.content ?? "");
    return (
      <div className="flex flex-col items-end gap-2 min-w-0">
        {noteId && (
          <div className="flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-600 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-300">
            <CornerUpLeft className="h-3 w-3" />
            {t("common.aiComments.replyingToNote")}
          </div>
        )}
        {text && (
          <div className="rounded-2xl bg-primary text-primary-foreground px-4 py-2.5 max-w-[75%] w-fit min-w-0 select-text">
            <p className="whitespace-pre-wrap break-words text-sm">{text}</p>
          </div>
        )}
        {media.length > 0 && (
          <div className="flex w-full max-w-[300px] flex-col gap-2">
            {media.map((m) => (
              <ChatMediaAttachment key={m.id} media={m} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="group/ai-msg flex flex-col items-start gap-3 max-w-[85%] min-w-0 w-full">
      {message.content && (
        <div className="prose prose-sm max-w-none w-full min-w-0 dark:prose-invert break-words select-text [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_img]:max-w-full [&_img]:h-auto [&_a]:[overflow-wrap:anywhere] [&_code]:[overflow-wrap:anywhere]">
          <Markdown remarkPlugins={REMARK_PLUGINS} components={MD_COMPONENTS}>
            {resolveAiText(translateProposalMessage(message.content, t), t)}
          </Markdown>
        </div>
      )}

      {!isStatsMode && message.execution_id && (
        <MessageToolsFooter
          executionId={message.execution_id}
          isStatsMode={isStatsMode}
        />
      )}

      {media.length > 0 && (
        <div className="flex w-full max-w-[300px] flex-col gap-2">
          {media.map((m) => (
            <ChatMediaAttachment key={m.id} media={m} />
          ))}
        </div>
      )}

      {richContents.map((rc, idx) =>
        // The guide chip is a self-contained pill — skip the chart/table card
        // frame so it doesn't sit inside an extra bordered rectangle.
        rc.type === "ui_guide" ? (
          <RichContentRenderer key={`${rc.type}-${idx}`} content={rc} />
        ) : (
          <div
            key={`${rc.type}-${idx}`}
            className="bg-card border rounded-lg p-4 w-full overflow-hidden"
          >
            <RichContentRenderer content={rc} />
          </div>
        ),
      )}

      {isWaitingForInput && onSendMessage && (
        <div className="w-full">
          <UserInputCard message={message} onSendMessage={onSendMessage} />
        </div>
      )}

      {message.proposal_batch_id && (
        <div className="w-full">
          <ProposalCard batchId={message.proposal_batch_id} />
        </div>
      )}
    </div>
  );
}

// Memoized: the chat re-renders on every SSE tick, but a row only needs to
// re-render when its own `message` (identity preserved per-id in useChatSession)
// or the small scalar props actually change — otherwise the whole list, plus
// each row's media attachments, re-render on every tick.
export const MessageBubble = memo(MessageBubbleComponent);
